import { entityAnalyticsConfig } from '@vedalib/courseAnalitycs/src/config'
import { debounce } from 'lodash'

import {
  getAllTextInBlock,
  transformBlockToHierarchy,
  transformToBlock,
} from 'components/Editor/EditorContext/selectors/blockAnalytic'
import { BlockModeEnum, ScormStatusMode } from 'gql/__generated__/graphql'
import { getBuildUrl, getWindowData } from 'services/ScormData/ScormData'
import { setCourseStatus } from 'services/Store/Project/actions'
import { SectionTypeEnum } from 'services/Store/Project/enums'
import {
  setInteractiveScorm,
  setReflectionScorm,
} from 'services/Store/Project/middlewares/scormInteractions'
import {
  getBlockQuestionIndex,
  getBlocks,
  getCourseState,
  getProgress,
  getSection,
  getSections,
  getTestScore,
  getCanRetryTest,
  getTestInfo,
} from 'services/Store/Project/selectors'

import { ILmsEvent } from '../types'
import { getTestStatuses } from '../utils/status.utils'
import { getBrowserInfo } from '../utils/xapi-statement.utils'
import type { IHandlerContext } from './lms.utils'
import { getOsInfo, getDeviceType, getLaunchUrl, resolveSourceContext } from './lms.utils'

export interface INavState {
  prevSectionId: string | null
  prevSectionName: string | null
  prevSectionType: SectionTypeEnum | null
  courseInitialized: boolean
  lastProgress: number
  completionSent: boolean
}

export const createNavState = (): INavState => ({
  prevSectionId: null,
  prevSectionName: null,
  prevSectionType: null,
  courseInitialized: false,
  lastProgress: -1,
  completionSent: false,
})

export const handleQuestionValidate = async (
  ctx: IHandlerContext,
  payload: { blockId?: string },
  finalTestId?: string,
): Promise<void> => {
  const { state, lms, scormSettings, commonVars } = ctx
  const section = getSection(state)
  const blockId = payload.blockId || commonVars.blockId

  if (!blockId) {
    return
  }

  const isTest = section?.type === SectionTypeEnum.test

  const masterType = lms.getMasterType()
  if (isTest && (masterType === 'scorm' || masterType === 'portal')) {
    setInteractiveScorm({ state, blockId })
  }

  const indexBlock = getBlockQuestionIndex(state, blockId) + 1
  const testInfo = getTestInfo(state, blockId)
  const scoreScore = getTestScore(state, finalTestId)
  const statuses = getTestStatuses(scoreScore, scormSettings, {
    canRetryTest: getCanRetryTest(state),
    testThreshold: section?.test?.testThreshold,
  })
  const completion = statuses.completionStatus === 'completed'

  const xapiPassed = entityAnalyticsConfig['courseQuestionValidate']
  const data = xapiPassed.answer({
    resultExtensions: {
      completion,
      score: {
        raw: Math.round(scoreScore.percent),
        min: 0,
        max: 100,
        scaled: scoreScore.scaled,
      },
    },
    objectExtensions: {
      total: scoreScore.total,
      percent: scoreScore.percent,
      success: scoreScore.success,
      elementId: testInfo?.elements[0]?.elementId,
      value: testInfo?.elements[0]?.value,
      indexBlock,
      completion,
      isValid: testInfo?.result?.isValid,
      progress: getProgress(state),
      ...commonVars,
    },
  })
  await lms.trackEvent(data)
}

export const handleTestEnd = async (ctx: IHandlerContext, finalTestId: string): Promise<void> => {
  const { state, lms, dispatch, scormSettings, commonVars } = ctx

  const score = getTestScore(state, finalTestId)
  const canRetry = getCanRetryTest(state)
  const section = getSection(state)
  const statuses = getTestStatuses(score, scormSettings, {
    canRetryTest: canRetry,
    testThreshold: section?.test?.testThreshold,
  })
  const raw = scormSettings.runtimeSettings?.scoreType === 'percent' ? score.percent : score.success
  const max = scormSettings.runtimeSettings?.scoreType === 'percent' ? 100 : score.total

  const { scaled, total, percent, success } = score

  await lms.setScore({ raw, min: 0, max, scaled })

  // Send completion event for xAPI (SCORM uses CMI via setCompletionStatus)
  if (statuses.completionStatus === 'completed') {
    const completedConfig = entityAnalyticsConfig['courseCompleted']
    const completedData = completedConfig.completed({
      resultExtensions: {
        completion: true,
        score: { raw: Math.round(percent), min: 0, max: 100, scaled },
      },
      objectExtensions: {
        completion: true,
        total,
        percent,
        success,
        progress: getProgress(state),
        ...commonVars,
      },
    })
    await lms.trackEvent(completedData)
  }

  if (statuses.successStatus === 'passed') {
    const config = entityAnalyticsConfig['coursePassed']
    const completion = statuses.completionStatus === 'completed'
    const data = config.passed({
      resultExtensions: {
        completion: statuses.completionStatus === 'completed',
        success: true,
        score: { raw: Math.round(percent), min: 0, max: 100, scaled },
      },
      objectExtensions: {
        ...commonVars,
        completion,
        total,
        percent,
        success,
        progress: getProgress(state),
      },
    })
    await lms.trackEvent(data)
  }
  if (statuses.successStatus === 'failed') {
    const config = entityAnalyticsConfig['courseFailed']
    const completion = statuses.completionStatus === 'completed'
    const data = config.failed({
      resultExtensions: {
        completion,
        success: false,
        score: { raw: Math.round(percent), min: 0, max: 100, scaled },
      },
      objectExtensions: {
        ...commonVars,
        completion,
        total,
        percent,
        success,
        progress: getProgress(state),
      },
    })
    await lms.trackEvent(data)
  }
  // SCORM 1.2 / WebTutor: Strict order is critical!
  // WebTutor closes session immediately after receiving 'completed' lesson_status
  // Order: Score (already set above) → Success → Completion → Commit
  await lms.setSuccessStatus(statuses.successStatus)
  await lms.setCompletionStatus(statuses.completionStatus)

  // Force immediate commit - don't wait for debounce timer
  await lms.commit()

  dispatch(setCourseStatus(statuses.completionStatus))
}

export const handleTestRestart = async (
  ctx: IHandlerContext,
  navState: INavState,
): Promise<void> => {
  const { state, lms, dispatch, commonVars } = ctx

  await lms.setScore({ raw: 0, min: 0, max: 0, scaled: 0 })
  await lms.setProgress(0)
  navState.lastProgress = 0
  navState.completionSent = false
  await lms.setSuccessStatus('unknown')
  await lms.setCompletionStatus('incomplete')
  dispatch(setCourseStatus('incomplete'))

  const xapiConfig = entityAnalyticsConfig['courseAttempted']
  const data = xapiConfig.attempted({
    resultExtensions: { completion: false, success: false },
    objectExtensions: { ...commonVars, progress: getProgress(state) },
  })
  await lms.trackEvent(data)
}

export const handleViewModeProgress = async (
  ctx: IHandlerContext,
  navState: INavState,
  progress: number,
): Promise<boolean> => {
  const { state, lms, dispatch, scormSettings, commonVars } = ctx

  // Only send progress if it changed
  const progressChanged = progress !== navState.lastProgress
  if (progressChanged) {
    await lms.setProgress(progress)
    navState.lastProgress = progress
  }

  let shouldCommit = false
  const threshold = scormSettings.runtimeSettings.courseThresholdFinish || 0
  const completed = progress >= threshold

  // Use navState.completionSent to prevent duplicate completion events
  if (completed && !navState.completionSent) {
    navState.completionSent = true

    // Send full courseCompleted xAPI event (matching handleTestEnd pattern)
    const courseCompletedConfig = entityAnalyticsConfig['courseCompleted']
    const completionData = courseCompletedConfig.completed({
      resultExtensions: {
        completion: true,
        score: { raw: Math.round(progress), min: 0, max: 100, scaled: progress / 100 },
      },
      objectExtensions: { completion: true, progress, ...commonVars },
    })
    await lms.trackEvent(completionData)

    shouldCommit = true
    if (scormSettings.runtimeSettings?.usedStatuses === ScormStatusMode.passedIncomplete) {
      // Send passed event for xAPI (SCORM uses CMI via setSuccessStatus)
      const passedConfig = entityAnalyticsConfig['coursePassed']
      const passedData = passedConfig.passed({
        resultExtensions: {
          completion: true,
          success: true,
          score: { raw: Math.round(progress), min: 0, max: 100, scaled: progress / 100 },
        },
        objectExtensions: { completion: true, progress, ...commonVars },
      })
      await lms.trackEvent(passedData)
      // SCORM 1.2 / WebTutor: Strict order is critical!
      // Order: Score → Success → Completion
      await lms.setScore({ raw: Math.round(progress), min: 0, max: 100, scaled: progress / 100 })
      await lms.setSuccessStatus('passed')
    }
    await lms.setCompletionStatus('completed')

    // Force immediate commit - don't wait for debounce timer
    await lms.commit()

    const courseState = getCourseState(state)
    if (courseState.status !== 'completed') {
      dispatch(setCourseStatus('completed'))
    }
  }

  return shouldCommit
}

const debouncedTrackEvent = debounce(async (reflectionData: ILmsEvent, ctx: IHandlerContext) => {
  await ctx.lms.trackEvent(reflectionData)
}, 1500)

export const handleReflection = async (
  ctx: IHandlerContext,
  payload: { elementId?: string; questionText?: string; value?: string },
): Promise<void> => {
  const { state, lms, commonVars } = ctx

  // Only handle SCORM suspend_data here - xAPI tracking is handled by the component
  // with proper debouncing and value inclusion (see ReflectionElement.tsx)
  const { elementId, questionText, value } = payload

  const masterType = lms.getMasterType()
  if (masterType === 'scorm' || masterType === 'portal') {
    setReflectionScorm({ state, elementId, questionText })
  }

  if (elementId) {
    const reflectionConfig = entityAnalyticsConfig['reflection']
    const reflectionData = reflectionConfig.interacted({
      elementId,
      resultExtensions: { label: questionText, value, elementId },
      objectExtensions: { elementId, progress: getProgress(state), ...commonVars },
    })

    debouncedTrackEvent(reflectionData, ctx)
  }
}

export const handleInitialized = async (
  ctx: IHandlerContext,
  navState: INavState,
  isViewMode: boolean,
): Promise<boolean> => {
  const { state, lms, scormSettings, commonVars } = ctx
  const project = state.data.project
  let shouldCommit = false

  if (!navState.courseInitialized) {
    navState.courseInitialized = true

    const sections = getSections(state)
    const currentSection = getSection(ctx.state)
    const cover = getSection(ctx.state, { cover: true })
    const firstSection = getSection(ctx.state, { first: true })
    const section = currentSection || cover || firstSection
    const courseStructure = {
      company: {
        companyName: project.company?.name || '',
        id: project.companyId,
        entity: 'company',
        exportId: scormSettings?.id,
        courseId: project.id,
        project: {
          entity: 'project',
          id: project.id,
          name: project.name,
          sections: sections.map((sec) => ({
            entityType: 'section',
            id: sec.id,
            name: sec.name,
            type: sec.type,
            blocks: getBlocks(state, sec.id).map((block) => ({
              entityType: 'block',
              id: block.uuid,
              name: block.name,
              mode: block.mode,
              ...(block.mode === BlockModeEnum.questions && {
                labels: getAllTextInBlock({ block }),
              }),
              elements: transformToBlock(transformBlockToHierarchy(block)),
            })),
          })),
        },
      },
    }

    const sourceContext = resolveSourceContext(scormSettings?.id || getWindowData()?.artifactId)

    const initConfig = entityAnalyticsConfig['courseInitialized']
    const initData = initConfig.initialized({
      elementId: project.id,
      resultExtensions: {
        projectData: JSON.stringify(courseStructure),
        browser: getBrowserInfo().name,
        language: project.locale || navigator.language,
        os: getOsInfo(),
        device: getDeviceType(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      objectExtensions: {
        ...commonVars,
        progress: getProgress(state),
        exportId: scormSettings?.id,
        exportDate: scormSettings?.exportDate || new Date().getTime(),
        height: window.outerHeight,
        width: window.outerWidth,
        launchUrl: getLaunchUrl(),
        buildUrl: getBuildUrl(),
        ...sourceContext,
      },
    })
    await lms.trackEvent(initData)

    if (section) {
      // Send enteredSection for initial section (prevSectionId is null on first start)
      const enteredConfig = entityAnalyticsConfig['enteredSection']
      const enteredData = enteredConfig.enteredSection({
        elementId: section.id,
        resultExtensions: {
          sectionType: section.type,
          sectionId: section.id,
          sectionName: section.name,
          courseId: project.id,
        },
        objectExtensions: { ...commonVars, sectionId: section.id, progress: getProgress(state) },
      })
      await lms.trackEvent(enteredData)

      navState.prevSectionId = section.id
      navState.prevSectionName = section.name || null
      navState.prevSectionType = section.type
    }

    // Set initial score AFTER initialized (xAPI events must come after initialization)
    if (
      isViewMode &&
      scormSettings.runtimeSettings?.usedStatuses === ScormStatusMode.passedIncomplete
    ) {
      await lms.setScore({ raw: 0, min: 0, max: 100, scaled: 0 })
      shouldCommit = true
    }
  }
  return shouldCommit
}

export const handleCourseStart = async (
  ctx: IHandlerContext,
  navState: INavState,
): Promise<void> => {
  const { state, lms, commonVars } = ctx
  const section = getSection(state, { cover: true })
  const progress = getProgress(state)

  // Set initial completion status (doesn't send xAPI event for 'incomplete')
  await lms.setCompletionStatus('incomplete')

  // Send started SECOND
  const xapiConfig = entityAnalyticsConfig['courseStart']
  const data = xapiConfig.started({
    resultExtensions: {
      completion: false,
      score: { raw: Math.round(progress), min: 0, max: 100, scaled: progress / 100 },
    },
    objectExtensions: { elementUrl: window.location.href, progress, ...commonVars },
  })
  await lms.trackEvent(data)

  if (section) {
    navState.prevSectionId = section.id
    navState.prevSectionName = section.name || null
    navState.prevSectionType = section.type
  }
}

export const handleNavigation = async (
  ctx: IHandlerContext,
  navState: INavState,
  currentSection: { id: string; name: string; type: SectionTypeEnum } | null,
): Promise<void> => {
  const { state, lms, commonVars } = ctx
  const project = ctx.state.data.project
  const progress = getProgress(state)

  const currentSectionId = currentSection?.id || null
  const currentSectionName = currentSection?.name || null
  const currentSectionType = currentSection?.type || null

  if (navState.prevSectionId && navState.prevSectionId !== currentSectionId) {
    const exitedConfig = entityAnalyticsConfig['exitedSection']
    const exitedData = exitedConfig.exitedSection({
      elementId: navState.prevSectionId,
      resultExtensions: {
        sectionType: navState.prevSectionType,
        sectionId: navState.prevSectionId,
        sectionName: navState.prevSectionName,
        courseId: project.id,
      },
      objectExtensions: { ...commonVars, sectionId: navState.prevSectionId, progress },
    })
    await lms.trackEvent(exitedData)
  }

  if (currentSectionId && currentSectionId !== navState.prevSectionId) {
    const enteredConfig = entityAnalyticsConfig['enteredSection']
    const enteredData = enteredConfig.enteredSection({
      elementId: currentSectionId,
      resultExtensions: {
        sectionType: currentSectionType,
        sectionId: currentSectionId,
        sectionName: currentSectionName,
        courseId: project.id,
      },
      objectExtensions: { ...commonVars, sectionId: currentSectionId, progress },
    })
    await lms.trackEvent(enteredData)
  }

  navState.prevSectionId = currentSectionId
  navState.prevSectionName = currentSectionName
  navState.prevSectionType = currentSectionType
}

export const handleCourseEnd = async (ctx: IHandlerContext, navState: INavState): Promise<void> => {
  const { state, lms, commonVars } = ctx
  const project = ctx.state.data.project
  const progress = getProgress(state)

  if (navState.prevSectionId) {
    const exitedConfig = entityAnalyticsConfig['exitedSection']
    const exitedData = exitedConfig.exitedSection({
      elementId: navState.prevSectionId,
      resultExtensions: {
        sectionType: navState.prevSectionType,
        sectionId: navState.prevSectionId,
        sectionName: navState.prevSectionName,
        courseId: project.id,
      },
      objectExtensions: { ...commonVars, sectionId: navState.prevSectionId, progress },
    })
    try {
      await lms.trackEvent(exitedData)
    } catch (error) {
      console.error('[LMS] Failed to track exited section event:', error)
    }
  }

  const xapiConfig = entityAnalyticsConfig['courseEnd']
  const data = xapiConfig.terminated({
    resultExtensions: {},
    objectExtensions: { ...commonVars, progress },
  })

  try {
    await lms.trackEvent(data)
  } catch (error) {
    console.error('[LMS] Failed to track course end event:', error)
  }

  try {
    await lms.commit()
  } catch (error) {
    console.error('[LMS] Failed to commit session:', error)
  }

  try {
    await lms.terminate()
  } catch (error) {
    console.error('[LMS] Failed to terminate session:', error)
  }
}
